// Post-login landing. Not a KPI dashboard — the URL is the artifact.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api.js";
import { formatClock } from "../lib/time.js";

export default function Dashboard() {
  const navigate = useNavigate();
  const [sess, setSess] = useState(null);
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await api("/me", { method: "GET" });
      if (cancelled) return;
      if (!res.ok || !res.body?.ok) {
        navigate("/", { replace: true });
        return;
      }
      setSess(res.body);
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleLogout = async () => {
    setLoggingOut(true);
    await api("/logout", { method: "POST" });
    navigate("/", { replace: true });
  };

  return (
    <main className="min-h-screen bg-aught-paper paper-tex flex flex-col">
      <header className="px-8 sm:px-12 py-8 flex items-center justify-between">
        <h1 className="font-serif text-3xl tracking-tightest text-aught-ink">
          Aught
        </h1>
        <button
          type="button"
          onClick={handleLogout}
          disabled={loggingOut}
          className="font-mono text-[11px] tracking-[0.16em] uppercase text-aught-ash hover:text-aught-ink transition-colors duration-200 ease-out-soft"
        >
          {loggingOut ? "Closing…" : "Sign out"}
        </button>
      </header>

      <section className="flex-1 flex items-center justify-center px-6 sm:px-10">
        <div className="max-w-2xl w-full text-center space-y-12">
          <div className="space-y-6">
            <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-aught-ash">
              Next route
            </p>
            <p className="font-mono tabular text-2xl sm:text-3xl text-aught-ink break-all">
              aught.app/console
            </p>
            <a
              href="/console"
              className="inline-block font-mono text-sm tracking-[0.16em] uppercase text-aught-oxide hover:text-aught-oxideDeep transition-colors duration-200 ease-out-soft"
            >
              Enter console →
            </a>
          </div>
        </div>
      </section>

      <footer className="px-8 sm:px-12 py-8 flex flex-wrap items-end justify-between gap-6">
        <FooterCell label="Session" value={sess?.user ?? "—"} />
        <FooterCell label="Issued" value={formatClock(sess?.issuedAt, { seconds: false })} />
        <FooterCell label="Expires" value={formatClock(sess?.expiresAt, { seconds: false })} />
        <FooterCell
          label="Token"
          value="••••••••••••••••"
          hint="The full token is HttpOnly; this is a shape placeholder."
        />
      </footer>
    </main>
  );
}

function FooterCell({ label, value, hint }) {
  return (
    <div className="space-y-1">
      <p className="font-mono text-[11px] tracking-[0.16em] uppercase text-aught-ash">
        {label}
      </p>
      <p className="font-mono tabular text-sm text-aught-ink">{value}</p>
      {hint && <p className="font-serif italic text-xs text-aught-ash/70">{hint}</p>}
    </div>
  );
}
